import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Markdown,
  TextField,
  Title,
  Typography,
} from "@qws-org/ui-kit";
import type { FC } from "react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { registrationSchema } from "~/features/registration-form/registration.schema";
import { Eye, EyeSlash } from "~/icons";

import styles from "./RegistrationForm.module.css";

export type RegistrationData = {
  form_title: string;
  form_description_text: string;
  form_email_input_label: string;
  form_nickname_input_label: string;
  form_password_input_label: string;
  form_promo_code_button_text: string;
  form_promo_code_input_label: string;
  form_remember_checkbox_text?: string;
  form_submit_button_text: string;
  validation_invalid_email: string;
  validation_invalid_password: string;
  validation_nickname_max_length: string;
  validation_nickname_min_length: string;
  validation_password_max_width: string;
  validation_password_min_width: string;
  validation_nickname_contain_password: string;
  validation_nickname_contains_only_numbers: string;
  validation_required: string;
  description_link_text: string;
  description_text: string;
  validation_email_max_length: string;
  validation_email_min_length: string;
  form_terms_description_label?: string;
  form_terms_description_text?: string;
};

export type RegistrationFormValues = {
  nick: string;
  email: string;
  password: string;
  bonusCode?: string;

  isRememberPassword?: boolean;
};

export type RegistrationServerErrors = Partial<
  Record<keyof RegistrationFormValues, string>
>;

export type RegistrationFormProps = {
  data: RegistrationData;
  onSubmit: (data: RegistrationFormValues) => void;
  serverErrors?: RegistrationServerErrors | null;
};

export const RegistrationForm: FC<RegistrationFormProps> = ({
  data,
  onSubmit,
  serverErrors,
}) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<RegistrationFormValues>({
    mode: "onSubmit",
    defaultValues: { isRememberPassword: false },
    resolver: yupResolver(registrationSchema(data)),
  });

  const [passwordIsVisible, setPasswordIsVisible] = useState(false);
  const [isBonusCodeOpen, setIsBonusCodeOpen] = useState(false);
  useEffect(() => {
    if (serverErrors) {
      Object.entries(serverErrors).forEach(([field, message]) => {
        //  если сервер отдал ошибку для "bonus-code", переназначаем ошибку на bonusCode
        const formField =
          field === "bonus-code"
            ? "bonusCode"
            : (field as keyof RegistrationFormValues);

        if (message) {
          setError(formField, {
            type: "server",
            message,
          });
        }
      });
    } else {
      clearErrors();
    }
  }, [serverErrors, setError, clearErrors]);

  return (
    <Card
      className="rounded-2xl md:rounded-lg "
      variant="secondary"
      md={{ p: { pb: 32 }, maxWidth: 525 }}
      minWidth={312}
      maxWidth={440}
      d={{ maxWidth: 525 }}
      data-testid="registration-form"
    >
      <Box p={{ px: 16, py: 24 }} md={{ p: { py: 32, px: 40 } }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title
            as="h2"
            size="small"
            weight="semibold"
            horizontalAlign="center"
            m={{ mb: 16 }}
            md={{ m: { mb: 24 } }}
            data-testid="registration-form-title"
          >
            {data.form_title}
          </Title>

          <Controller
            control={control}
            name="nick"
            render={({ field }) => (
              <TextField
                {...field}
                label={data.form_nickname_input_label}
                errorMessage={errors.nick?.message}
                data-testid="registration-form-nickname-input"
              />
            )}
          />

          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <TextField
                {...field}
                label={data.form_email_input_label}
                errorMessage={errors.email?.message}
                data-testid="registration-form-email-input"
              />
            )}
          />

          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <TextField
                {...field}
                label={data.form_password_input_label}
                type={passwordIsVisible ? "text" : "password"}
                errorMessage={errors.password?.message}
                icon={
                  passwordIsVisible ? (
                    <EyeSlash
                      onClick={() => setPasswordIsVisible(!passwordIsVisible)}
                      className="cursor-pointer"
                      viewBox="0 0 24 24"
                    />
                  ) : (
                    <Eye
                      onClick={() => setPasswordIsVisible(!passwordIsVisible)}
                      className="cursor-pointer"
                      viewBox="0 0 24 24"
                    />
                  )
                }
                data-testid="registration-form-password-input"
              />
            )}
          />

          {!isBonusCodeOpen ? (
            <Button
              variant="transparent"
              type="button"
              onPress={() => setIsBonusCodeOpen(true)}
              size="medium"
              m={{ mb: 16 }}
              md={{ m: { mb: 24 } }}
              width="100%"
              maxHeight="none"
              minHeight="none"
              height="fit-content"
              className=" text-center underline border-none hover:bg-transparent hover:text-[var(--colors-surface-primary-default)]"
              data-testid="registration-form-promo-code-button"
            >
              {data.form_promo_code_button_text}
            </Button>
          ) : (
            <Controller
              control={control}
              name="bonusCode"
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                  m={{ mb: 16 }}
                  md={{ m: { mb: 24 } }}
                  label={data.form_promo_code_input_label}
                  errorMessage={errors?.bonusCode?.message}
                  data-testid="registration-form-promo-code-input"
                />
              )}
            />
          )}

          <Controller
            control={control}
            name="isRememberPassword"
            rules={{ required: false }}
            render={({ field, fieldState: { error } }) => (
              <Box m={{ mb: 16 }} md={{ m: { mb: 24 } }}>
                <Checkbox
                  aria-label="label"
                  errorMessage={error?.message}
                  data-testid="registration-form-remember-password-checkbox"
                  {...field}
                >
                  <Typography
                    weight="normal"
                    size="small"
                    md={{ size: "medium" }}
                    as="span"
                  >
                    {data?.form_remember_checkbox_text ?? "Remember password"}
                  </Typography>
                </Checkbox>
              </Box>
            )}
          />

          <Markdown
            className={styles.formDescriptionMarkdown}
            data-testid="registration-form-description"
          >
            {data.form_description_text}
          </Markdown>

          <Button
            type="submit"
            size="small"
            weight="bold"
            md={{
              fontSize: "button.desktop.medium",
              p: { py: 17 },
            }}
            p={{ py: 12 }}
            width="100%"
            maxHeight="none"
            minHeight="none"
            height="fit-content"
            isDisabled={Object.keys(errors).length > 0}
            data-testid="registration-form-submit-button"
          >
            {data.form_submit_button_text}
          </Button>
        </form>
      </Box>
    </Card>
  );
};
